import React, { useEffect, useState } from "react";
import { Dialog, AppBar, Toolbar, Typography, Button, Box, List, ListItem, ListItemText } from "@mui/material";
import { useDataScienceService } from "../../../../use-cases/data-science";
import { FixedSizeList as VirtualList } from "react-window";
import { formatCnpj } from "../../../../utils/formatters/cnpj";

interface ModelDetailsProps {
  open: boolean;
  onClose: () => void;
  modelId: string | null;
}

interface ModelDetailsData {
  _id: string;
  id: string;
  model_name: string;
  model_type: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  cnpjs?: string[];
  bad_data?: { cnpj: string; [key: string]: any }[];
  good_data?: { cnpj: string; [key: string]: any }[];
  // ...outros campos
}

export const ModelDetails: React.FC<ModelDetailsProps> = ({ open, onClose, modelId }) => {
  const { getModelDetails } = useDataScienceService();
  const [details, setDetails] = useState<ModelDetailsData | null>(null);

  useEffect(() => {
    if (open && modelId) {
      setDetails(null); // Limpa os detalhes imediatamente ao abrir ou trocar de modelo
      getModelDetails(modelId)
        .then((res) => {
          setDetails(res?.model ?? null)
        })
        .catch(() => setDetails(null));
    }
  }, [open, modelId, getModelDetails]);

  function formatCNPJ(cnpj: string) {
    // Usa a função centralizada do cnpjFormatter que suporta
    // tanto o formato numérico antigo quanto o alfanumérico novo (RF 2026)
    return formatCnpj(cnpj);
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Detalhes do Modelo
          </Typography>
          <Button autoFocus color="inherit" onClick={onClose}>
            Fechar
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 4 }}>
        {!details ? (
          <Typography>Carregando…</Typography>
        ) : (
          <List>
            <ListItem>
              <ListItemText primary="Nome" secondary={details.model_name} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tipo" secondary={details.model_type} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Criado por" secondary={details.createdByName || details.createdBy} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Data de Criação" secondary={new Date(details.createdAt).toLocaleString()} />
            </ListItem>
            {/* Lista de CNPJs Positivos */}
            {details.good_data && details.good_data.length > 0 ? (
              <ListItem alignItems="flex-start" sx={{ display: 'block' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  CNPJs Positivos
                </Typography>
                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                  <Box sx={{ height: 300, width: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: 8, textAlign: 'left' }}>CNPJ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <VirtualList
                          height={260}
                          width={"100%"}
                          itemCount={details.good_data.length}
                          itemSize={32}
                          itemData={details.good_data.map(d => d.cnpj)}
                        >
                          {({ index, style, data }) => (
                            <tr
                              style={{
                                ...style,
                                background: index % 2 === 0 ? "#f5f5f5" : "#fff"
                              }}
                              key={index}
                            >
                              <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                                <Typography variant="body2">{formatCNPJ(data[index])}</Typography>
                              </td>
                            </tr>
                          )}
                        </VirtualList>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </ListItem>
            ) : (
              <ListItem alignItems="flex-start" sx={{ display: 'block' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  CNPJs Positivos
                </Typography>
                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', mb: 3, p: 2, textAlign: 'center', color: 'text.secondary', bgcolor: '#fafafa' }}>
                  Nenhum CNPJ positivo disponível
                </Box>
              </ListItem>
            )}
            {/* Lista de CNPJs Negativos */}
            {details.bad_data && details.bad_data.length > 0 ? (
              <ListItem alignItems="flex-start" sx={{ display: 'block' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  CNPJs Negativos
                </Typography>
                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ height: 300, width: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: 8, textAlign: 'left' }}>CNPJ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <VirtualList
                          height={260}
                          width={"100%"}
                          itemCount={details.bad_data.length}
                          itemSize={32}
                          itemData={details.bad_data.map(d => d.cnpj)}
                        >
                          {({ index, style, data }) => (
                            <tr
                              style={{
                                ...style,
                                background: index % 2 === 0 ? "#f5f5f5" : "#fff"
                              }}
                              key={index}
                            >
                              <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                                <Typography variant="body2">{formatCNPJ(data[index])}</Typography>
                              </td>
                            </tr>
                          )}
                        </VirtualList>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </ListItem>
            ) : (
              <ListItem alignItems="flex-start" sx={{ display: 'block' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  CNPJs Negativos
                </Typography>
                <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', p: 2, textAlign: 'center', color: 'text.secondary', bgcolor: '#fafafa' }}>
                  Nenhum CNPJ negativo disponível
                </Box>
              </ListItem>
            )}
            {/* Adicione outros campos conforme o retorno do backend */}
          </List>
        )}
      </Box>
    </Dialog>
  );
};